import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCancelChatAccessRequest,
  useChatAccessRequests,
  useCreateChatAccessRequest,
  useSendDraftFlowMessage,
} from "../../../../lib/react-query/aiChat";
import { queryKeys } from "../../../../redux/network/constant";
import { showToast } from "../../../../components/shared";
import { extractBackendErrorMessage } from "../../../../utils/utils";
import { cn } from "../../../../lib/cn";

const MESSAGE_PAGE = 1;
const MESSAGE_PAGE_SIZE = 200;

export const FileAccessRequestCard = ({ message, sessionId, resolved = false }) => {
  const queryClient = useQueryClient();
  const { data: accessRequests = [] } = useChatAccessRequests(sessionId);
  const { mutateAsync: createAccessRequest } = useCreateChatAccessRequest();
  const { mutateAsync: cancelAccessRequest } = useCancelChatAccessRequest();
  const { mutateAsync: sendMessage } = useSendDraftFlowMessage();
  const [state, setState] = useState(resolved ? "resolved" : "idle");
  const actionTakenRef = useRef(false);
  const retryStartedRef = useRef(false);

  const request = message?.metadata?.access_request || {};
  const files = useMemo(
    () => (Array.isArray(request?.files) ? request.files : []),
    [request?.files],
  );
  const question = request?.question || "";
  const requestByFileId = useMemo(
    () => new Map(accessRequests.map((item) => [String(item.file_id), item])),
    [accessRequests],
  );
  const filesWithState = useMemo(
    () => files.map((file) => {
      const existing = requestByFileId.get(String(file.file_id));
      return {
        ...file,
        requestId: existing?.id || file.request_id,
        requestStatus: existing?.status || file.request_status || "NONE",
      };
    }),
    [files, requestByFileId],
  );
  const requestableFiles = filesWithState.filter(
    (file) => file.can_contact_owner && file.requestStatus !== "APPROVED",
  );
  const pendingRequests = filesWithState.filter(
    (file) => file.requestStatus === "PENDING" && file.requestId,
  );
  const approvedFiles = filesWithState.filter(
    (file) => file.requestStatus === "APPROVED",
  );
  const resolvedWithoutApproval = filesWithState.some((file) =>
    ["DENIED", "EXPIRED", "CANCELLED"].includes(file.requestStatus),
  );

  const refreshMessages = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.AIChatMessages(sessionId, MESSAGE_PAGE), MESSAGE_PAGE_SIZE],
    });
  }, [queryClient, sessionId]);

  useEffect(() => {
    if (resolved) {
      actionTakenRef.current = true;
      setState("resolved");
    }
  }, [resolved]);

  useEffect(() => {
    if (resolved || retryStartedRef.current || !question || !approvedFiles.length) return;
    retryStartedRef.current = true;
    setState("allowing");
    sendMessage({
      sessionId,
      payload: {
        content: question,
        metadata: {
          file_access_retry: true,
          retry_of_message_id: request?.request_message_id || message?.id,
        },
      },
    })
      .then(() => {
        setState("allowed");
        refreshMessages();
      })
      .catch((err) => {
        retryStartedRef.current = false;
        setState("idle");
        showToast(
          extractBackendErrorMessage(err, "Failed to continue after approval"),
          "error",
          { position: "top-center" },
        );
      });
  }, [approvedFiles.length, message?.id, question, refreshMessages, request?.request_message_id, resolved, sendMessage, sessionId]);

  useEffect(() => {
    if (!pendingRequests.length && !approvedFiles.length && resolvedWithoutApproval && state === "waiting") {
      actionTakenRef.current = false;
      setState("idle");
    }
  }, [approvedFiles.length, pendingRequests.length, resolvedWithoutApproval, state]);

  const continueWithoutFiles = useCallback(async () => {
    if (actionTakenRef.current || !sessionId || !question) return;
    actionTakenRef.current = true;
    setState("denied");
    try {
      await sendMessage({
        sessionId,
        payload: {
          content: question,
          metadata: {
            skip_file_access_request: true,
            retry_of_message_id: request?.request_message_id || message?.id,
          },
        },
      });
      refreshMessages();
    } catch (err) {
      actionTakenRef.current = false;
      setState("idle");
      showToast(
        extractBackendErrorMessage(err, "Failed to continue without files"),
        "error",
        { position: "top-center" },
      );
    }
  }, [message?.id, question, refreshMessages, request?.request_message_id, sendMessage, sessionId]);

  const askForAccess = useCallback(async () => {
    if (actionTakenRef.current || !sessionId || !question || requestableFiles.length === 0) return;
    actionTakenRef.current = true;
    setState("asking");
    try {
      for (const file of requestableFiles) {
        await createAccessRequest({
          sessionId,
          payload: {
            file_id: file.file_id,
          },
        });
      }
      setState("waiting");
      refreshMessages();
    } catch (err) {
      actionTakenRef.current = false;
      setState("idle");
      showToast(
        extractBackendErrorMessage(err, "Failed to ask the file owner"),
        "error",
        { position: "top-center" },
      );
    }
  }, [createAccessRequest, question, refreshMessages, requestableFiles, sessionId]);

  const cancelPendingRequests = useCallback(async () => {
    if (actionTakenRef.current || pendingRequests.length === 0) return;
    actionTakenRef.current = true;
    setState("cancelling");
    try {
      for (const file of pendingRequests) {
        await cancelAccessRequest({ requestId: file.requestId, sessionId });
      }
      setState("cancelled");
      refreshMessages();
    } catch (err) {
      actionTakenRef.current = false;
      setState("waiting");
      showToast(
        extractBackendErrorMessage(err, "Failed to cancel the request"),
        "error",
        { position: "top-center" },
      );
    }
  }, [cancelAccessRequest, pendingRequests, refreshMessages, sessionId]);

  if (!files.length) return null;

  const waiting = state === "waiting" || pendingRequests.length > 0;
  const disabled = state !== "idle" || waiting;

  return (
    <div className="mt-2 rounded-lg border border-primary/15 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-primary">File access needed</p>
          <p className="mt-0.5 text-xs text-grey">
            I found files that may help. Approve before I read them.
          </p>
        </div>
        {disabled ? (
          <span className="rounded-full bg-surface-active px-2 py-0.5 text-[11px] text-mediumgrey">
            {state === "asking" ? "Asking owner..." : waiting ? "Waiting for owner" : "Done"}
          </span>
        ) : null}
      </div>
      <div className="mt-2 space-y-1">
        {files.map((file) => (
          <div key={file.file_id}>
            <div className="flex items-center justify-between gap-2 rounded-md bg-surface-active/70 px-2 py-1">
              <span className="min-w-0 truncate text-xs font-medium text-primary">
                {file.name}
              </span>
              <span className="shrink-0 text-[11px] text-mediumgrey">
                {[file.category, file.folder_name].filter(Boolean).join(" / ")}
              </span>
            </div>
            {file.owner?.name ? (
              <p className="px-2 text-[11px] text-mediumgrey">
                Owner: {file.owner.name}
              </p>
            ) : (
              <p className="px-2 text-[11px] text-mediumgrey">
                Owner unavailable
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={askForAccess}
          disabled={disabled || requestableFiles.length === 0}
          className={cn(
            "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-white",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          {state === "asking" ? "Asking owner..." : "Ask for access"}
        </button>
        <button
          type="button"
          onClick={waiting ? cancelPendingRequests : continueWithoutFiles}
          disabled={state === "asking" || state === "allowing" || state === "cancelling"}
          className={cn(
            "rounded-md border border-primary/15 px-2.5 py-1 text-xs font-medium text-primary",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          {waiting ? (state === "cancelling" ? "Cancelling..." : "Cancel request") : "Continue without file"}
        </button>
      </div>
    </div>
  );
};
